import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/components/AuthProvider';
import { Mail, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { signInWithEmailAndPassword, signInWithGoogle } from '@/lib/auth';
import { showNotification } from '@/lib/generalNotifications';
import { ForgotPasswordDialog } from '@/components/ForgotPasswordDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getBrandingConfig } from '@/utils/branding';
import { login as keycloakLogin } from '@/services/keycloak';
import { getAuthConfig } from '@/config/authConfig';

// Define the structure of location state
interface LocationState {
  email?: string;
  message?: string;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);
  const brandingConfig = getBrandingConfig();

  // Get auth configuration from environment
  const authConfig = getAuthConfig();

  // Add refs to prevent focus loss
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Removed auto-redirect to prevent infinite loops when user is not in database

  // Handle state passed from signup page
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.email) {
      setEmail(state.email);
    }
    if (state?.message) {
      setWelcomeMessage(state.message);
    }
  }, [location.state]);

  // Redirect if already logged in - add loading check to prevent premature redirects
  useEffect(() => {
    const anyLoading = isLoadingEmail || isLoadingGoogle || isLoadingSSO;
    if (user && !authLoading && !anyLoading) {
      // Use a small delay to prevent interrupting form input
      const timer = setTimeout(() => {
        // Get the redirect path from location state or query params, fallback to /app
        const state = location.state as { from?: { pathname: string } };
        const searchParams = new URLSearchParams(location.search);
        const redirectPath = state?.from?.pathname || searchParams.get('redirect') || '/app';
        navigate(redirectPath, { replace: true });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [user, navigate, authLoading, isLoadingEmail, isLoadingGoogle, isLoadingSSO, location]);


  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form fields before attempting login
    if (!email || !password) {
      showNotification({
        title: "Validation Error",
        message: "Please enter both email and password",
        type: "error"
      });
      return;
    }

    setIsLoadingEmail(true);

    try {
      await signInWithEmailAndPassword(email, password);
      // Don't navigate here - let the useEffect handle it after user data is loaded
    } catch (error) {
      console.error('Login error:', error);

      // Handle specific Firebase auth errors
      let errorMessage = "Failed to login";

      if (error instanceof Error) {
        switch (error.message) {
          case 'Firebase: Error (auth/user-not-found).':
            errorMessage = "No account found with this email address";
            break;
          case 'Firebase: Error (auth/wrong-password).':
            errorMessage = "Incorrect password";
            break;
          case 'Firebase: Error (auth/invalid-email).':
            errorMessage = "Invalid email address format";
            break;
          case 'Firebase: Error (auth/user-disabled).':
            errorMessage = "This account has been disabled";
            break;
          case 'Firebase: Error (auth/too-many-requests).':
            errorMessage = "Too many failed attempts. Please try again later";
            break;
          default:
            errorMessage = error.message.replace('Firebase: Error ', '').replace(/\(auth\/|\)|\./g, '');
        }
      }

      showNotification({
        title: "Login Failed",
        message: errorMessage,
        type: "error"
      });
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoadingGoogle(true);

    try {
      await signInWithGoogle();
      // Don't navigate here - let the useEffect handle it after user data is loaded
    } catch (error) {
      console.error('Google login error:', error);

      let errorMessage = "Failed to login with Google";

      if (error instanceof Error) {
        switch (error.message) {
          case 'Firebase: Error (auth/popup-closed-by-user).':
            errorMessage = "Login cancelled";
            break;
          case 'Firebase: Error (auth/popup-blocked).':
            errorMessage = "Popup blocked. Please allow popups and try again";
            break;
          case 'Firebase: Error (auth/cancelled-popup-request).':
            errorMessage = "Login cancelled";
            break;
          default:
            errorMessage = error.message.replace('Firebase: Error ', '').replace(/\(auth\/|\)|\./g, '');
        }
      }

      showNotification({
        title: "Login Failed",
        message: errorMessage,
        type: "error"
      });
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleSSOLogin = () => {
    setIsLoadingSSO(true);
    // Keycloak will redirect, so loading state will persist
    keycloakLogin();
  };

  // Show loading screen when auth is being verified (returning from Keycloak redirect)
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 text-lg">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Left panel - Branding and Features Showcase */}
      <div className="hidden lg:block w-1/2 bg-gradient-to-br from-primary to-primary/80 p-8 overflow-hidden relative">
        {/* Abstract shapes for visual interest */}
        <div className="absolute top-[10%] left-[15%] w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute bottom-[20%] right-[10%] w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>

        <div className="relative z-10 h-full flex flex-col justify-between">
          {/* Branding */}
          <div className="mb-10">
            <h1 className="text-white text-4xl font-bold mb-2">Invoicing System</h1>
            <p className="text-white/80 text-lg">Streamline your financial operations</p>
          </div>

          {/* Feature showcase */}
          <div className="space-y-6 mb-10">
            <h2 className="text-white text-2xl font-semibold">Powerful tools for your business</h2>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white/20 backdrop-blur-md rounded-xl p-6 transition-all hover:bg-white/30">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-2">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-medium mb-2">Professional Invoicing</h3>
                    <p className="text-white/80">Create and send customized invoices in seconds</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/20 backdrop-blur-md rounded-xl p-6 transition-all hover:bg-white/30">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-2">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-medium mb-2">Secure Payments</h3>
                    <p className="text-white/80">Process payments securely with integrated payment gateways</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/20 backdrop-blur-md rounded-xl p-6 transition-all hover:bg-white/30">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-2">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-medium mb-2">Financial Insights</h3>
                    <p className="text-white/80">Track your finances with detailed reporting and analytics</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-white/60 text-sm">
            © {new Date().getFullYear()} {brandingConfig.isInvoicerGG ? 'Monarch Labs, LLC' : 'Invoicing System'}. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md border-0 shadow-none bg-white dark:bg-gray-900 rounded-2xl overflow-hidden">
          <div className="p-8 md:p-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Welcome back</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Sign in to your account to continue</p>
            </div>

            {welcomeMessage && (
              <Alert className="mb-6 border-green-500/50 bg-green-50 dark:bg-green-950/20">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700 dark:text-green-400">
                  {welcomeMessage}
                </AlertDescription>
              </Alert>
            )}

            {authConfig.allowKeycloak && !authConfig.allowFirebase && !authConfig.allowGoogle ? (
              <div className="space-y-6">
                <Button
                  type="button"
                  onClick={handleSSOLogin}
                  disabled={isLoadingSSO}
                  className="w-full h-14 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 dark:text-gray-900"
                >
                  {isLoadingSSO ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white dark:border-gray-900 mr-2"></div>
                      Redirecting to SSO...
                    </div>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                        <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="currentColor"/>
                      </svg>
                      Sign in with SSO
                    </>
                  )}
                </Button>

                <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                  Use your organization credentials to sign in
                </p>
              </div>
            ) : (
              <>
                {authConfig.allowFirebase && (
                  <>
                    <form onSubmit={handleEmailLogin} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</Label>
                        <EnhancedInput
                          id="email"
                          ref={emailInputRef}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          leftIcon={<Mail className="h-5 w-5" />}
                          required
                          autoComplete="email"
                          disabled={isLoadingEmail}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</Label>
                          <button
                            type="button"
                            onClick={() => setForgotPasswordOpen(true)}
                            className="text-sm text-primary hover:underline focus:outline-none"
                            disabled={isLoadingEmail}
                          >
                            Forgot password?
                          </button>
                        </div>
                        <EnhancedInput
                          id="password"
                          ref={passwordInputRef}
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          leftIcon={<Lock className="h-5 w-5" />}
                          required
                          autoComplete="current-password"
                          disabled={isLoadingEmail}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                        disabled={isLoadingEmail}
                      >
                        {isLoadingEmail ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Signing in...
                          </div>
                        ) : (
                          <>
                            Sign in
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}

                {(authConfig.allowGoogle || authConfig.allowKeycloak) && (
                  <div className="mt-6">
                    <Separator className="my-6">
                      <span className="px-2 text-gray-500 text-sm">Or continue with</span>
                    </Separator>

                    <div className="space-y-3">
                      {authConfig.allowGoogle && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGoogleLogin}
                          disabled={isLoadingGoogle}
                          className="w-full h-12 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Google
                        </Button>
                      )}

                      {authConfig.allowKeycloak && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleSSOLogin}
                          disabled={isLoadingSSO}
                          className="w-full h-12 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          {isLoadingSSO ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 dark:border-white mr-2"></div>
                              Redirecting to SSO...
                            </div>
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                                <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="currentColor"/>
                              </svg>
                              Sign in with SSO
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {authConfig.allowKeycloak && (
                      <p className="text-sm text-center text-gray-600 dark:text-gray-400 mt-3">
                        Use your organization credentials to sign in
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

          </div>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        isOpen={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />
    </div>
  );
}
